import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SettingsLink from "crqlar-react-common/dist/components/SettingsLink";
import {thinScrollbarSx} from "crqlar-react-common/dist/utils/scrollbar.util";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {useSettingsAccess} from "../useSettingsAccess";
import SettingsSearchDialog from "../settings/search/SettingsSearchDialog";
import {useSettingsSearchConfig} from "../settings/search/useSettingsSearchConfig";

const SettingsSidebar = () => {
    const {t} = useTranslation()
    const {sections, hasGuestAppModule, canAccessSettingsItem} = useSettingsAccess()
    const sidebarRef = React.useRef<HTMLDivElement>(null)
    const [scrolled, setScrolled] = React.useState(false)
    const searchConfig = useSettingsSearchConfig({sections, hasGuestAppModule, canAccessSettingsItem})

    const updateScrolled = React.useCallback(() => {
        setScrolled((sidebarRef.current?.scrollTop || 0) > 0)
    }, [])

    return (
        <Box
            ref={sidebarRef}
            onScroll={updateScrolled}
            sx={{
                height: '100%',
                overflowY: 'auto',
                ...thinScrollbarSx,
                minWidth: 240,
                width: 240,
                bgcolor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'grey.100',
                px: 2,
                pb: 6,
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    mx: -2,
                    px: 2,
                    height: 72,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.32)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: '1px solid',
                    borderColor: scrolled ? 'grey.100' : 'transparent',
                    transition: 'border-color 150ms',
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{width: '100%'}}>
                    <Button
                        component={RouterLink}
                        to="/"
                        variant="outlined"
                        startIcon={<ArrowBackIcon fontSize="small"/>}
                        sx={{
                            height: 36,
                            justifyContent: 'flex-start',
                            borderColor: 'grey.200',
                            color: 'text.primary',
                            bgcolor: 'background.paper',
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 1.25,
                            '&:hover': {
                                borderColor: 'grey.300',
                                bgcolor: 'grey.100',
                            },
                        }}
                    >
                        <Box component="span" sx={{display: 'inline-block', mt: '2px', ml: '-1px', lineHeight: 1}}>
                            {t('Back to app')}
                        </Box>
                    </Button>

                    <SettingsSearchDialog {...searchConfig}/>
                </Stack>
            </Box>

            <Stack gap={3} pt={1}>
                {sections.map(section => (
                    <Stack key={section.id} gap={1}>
                        <Typography sx={{opacity: 0.5, pl: 1.25}} variant="subtitle1">
                            {t(section.label)}
                        </Typography>

                        {section.items.map(item => (
                            <SettingsLink key={item.id} to={item.path} label={t(item.label)}/>
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Box>
    )
}

export default SettingsSidebar
